<script setup lang="ts" generic="T extends Tab">
import { useElementBounding, useParentElement } from '@vueuse/core';
import { computed, ref, watch } from 'vue';
import { type RouteLocationRaw, RouterLink } from 'vue-router';

export interface Tab {
  name: string;
  text: string;
  to?: RouteLocationRaw;
}

const props = withDefaults(
  defineProps<{
    active?: string;
    tabs: readonly T[];
    fill?: boolean;
    equalWidth?: boolean;
    direction?: 'vertical' | 'horizontal';
  }>(),
  {
    active: undefined,
    fill: false,
    equalWidth: false,
    direction: 'horizontal',
  },
);

const emit = defineEmits<{
  (event: 'update:active', tab: string): void;
}>();

const isVertical = computed(() => props.direction === 'vertical');
const isHorizontal = computed(() => props.direction === 'horizontal');

const activeTabElement = ref<HTMLElement>();
const tabBounds = useElementBounding(activeTabElement);
const containerBounds = useElementBounding(useParentElement(activeTabElement));

const markerPosition = computed(() => ({
  x: tabBounds.left.value - containerBounds.left.value,
  y: tabBounds.top.value - containerBounds.top.value,
}));

const markerSize = computed(() => ({
  width: isHorizontal.value ? tabBounds.width.value : 3,
  height: isVertical.value ? tabBounds.height.value : 3,
}));

const animate = ref(false);

watch(
  () => containerBounds.x.value && containerBounds.y.value,
  () => (animate.value = false),
);
watch(markerPosition, () => (animate.value = true));
</script>

<template>
  <div
    class="tml-tabs relative flex border-[--tml-tabs-border-color]"
    :class="{
      'border-b-[length:--tml-tabs-border-width] gap-1': isHorizontal,
      'flex-col': isVertical,
    }"
  >
    <component
      :is="tab.to ? RouterLink : 'button'"
      v-for="tab in tabs"
      :ref="
        (el: HTMLElement) => {
          if (tab.name === active) {
            activeTabElement = el;
          }
        }
      "
      :key="tab.name"
      :to="tab.to"
      class="tml-tab cursor-pointer transition text-left focus-visible:ring-2 focus-visible:ring-[--tml-tab-active-border-color] focus-visible:ring-inset"
      :class="{
        active: tab.name === active,
        grow: fill,
        'rounded-t-[--tml-tab-radius] whitespace-nowrap': isHorizontal,
        'flex-1': props.equalWidth && isHorizontal,
        'overflow-hidden': props.equalWidth && isHorizontal,
      }"
      replace
      @click.stop="emit('update:active', tab.name)"
    >
      <slot name="tab" :tab="tab">
        <div
          class="font-bold px-8 py-2"
          :class="{
            'text-center': isHorizontal,
            'text-left pl-4': isVertical,
          }"
        >
          {{ tab.text }}
        </div>
      </slot>
    </component>
    <div
      v-if="markerSize.width > 0 && markerSize.height > 0"
      class="absolute left-0 h-[--tml-tab-active-border-width] rounded-full bg-[--tml-tab-active-border-color]"
      :class="{
        'bottom-[-1px]': isHorizontal,
        'top-0': isVertical,
        'transition-[width,transform]': animate,
      }"
      :style="{
        transform: `translate(${isHorizontal ? markerPosition.x : 0}px, ${isVertical ? markerPosition.y : 0}px)`,
        width: `${markerSize.width}px`,
        height: `${markerSize.height}px`,
      }"
    ></div>
  </div>
</template>

<style scoped>
.tml-tab {
  @apply !text-[--tml-tab-text-color] bg-[--tml-tab-background-color];
  @apply outline outline-[length:--tml-tab-border-width] outline-[--tml-tab-border-color];
  @apply hover:bg-[--tml-tab-hover-background-color];
}
.tml-tab.active {
  @apply bg-[--tml-tab-active-background-color];
  @apply hover:bg-[--tml-tab-hover-background-color];
}
</style>
